import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Field, Form, FormElement, FormRenderProps } from "@progress/kendo-react-form";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Error, Label } from "@progress/kendo-react-labels";
import { useEffect, useState } from "react";
import notificationsApi from "../../api/notifications";
import { useApi } from "../../hooks/useApi";
import { capitalizeFirstLetter } from "../../utilities/textUtilities";
import { requiredValidator } from "../../validators";
import { FormDateTimePicker, FormDropDownList, FormInput } from "../form";
import { NotificationBanner, Severity } from "../layout/NotificationBanner";
import { LoadingIndicator } from "../LoadingIndicator";

interface AddEditAlertFormProps {
  cancelEdit: () => void;
  onSubmit: () => void;
  item: any;
}

export const AddEditAlertDialog = (props: AddEditAlertFormProps) => {
  const { item } = props;
  const severityList = Object.values(Severity).map((s) => {
    return { value: s, label: capitalizeFirstLetter(s) };
  });
  item.severitySelection = severityList.find((s) => s.value === item.severity);
  item.start = item.startDate ? new Date(item.startDate) : new Date();
  item.end = item.endDate ? new Date(item.endDate) : new Date();

  const saveNotificationApi = useApi(notificationsApi.save);
  const [isLoading, setIsLoading] = useState(false);
  const [isIndefinite, setIsIndefinite] = useState(item.endDate ? false : true);
  const [preview, setPreview] = useState(item);

  useEffect(() => {
    setIsLoading(saveNotificationApi.loading);
    if (!saveNotificationApi.loading && (saveNotificationApi.data || saveNotificationApi.error))
      if (!saveNotificationApi.error) props.onSubmit();
  }, [saveNotificationApi.loading, saveNotificationApi.data, saveNotificationApi.error]);

  const handleClickIndefinite = (e: any, formRenderProps: FormRenderProps) => {
    const { value } = e;
    setIsIndefinite(value);
    formRenderProps.onChange("end", { value: new Date() });
  };

  const handleMessageChange = (e: any) => {
    setPreview({ ...preview, message: e.target.value });
  };

  const handleSeverityChange = (e: any) => {
    setPreview({ ...preview, severity: e.target.value.value as Severity });
  };

  const handleSubmitEdit = async (event: any) => {
    const editItem = { ...event };
    editItem.severity = event.severitySelection.value;
    editItem.startDate = event.start;
    editItem.endDate = isIndefinite ? null : event.end;
    delete editItem.severitySelection;
    delete editItem.start;
    delete editItem.end;

    await saveNotificationApi.request(editItem);
  };

  return (
    <Dialog
      title={item.notificationId > 0 ? "Edit Alert" : "Add New Alert"}
      onClose={props.cancelEdit}
    >
      <div style={{ minWidth: 400 }}>
        <Form
          onSubmit={handleSubmitEdit}
          initialValues={item}
          render={(formRenderProps) => (
            <FormElement>
              <Field
                label="Message"
                name="message"
                component={FormInput}
                validator={requiredValidator}
                disabled={isLoading || item.isSystem}
                onChange={handleMessageChange}
              />

              <Field
                label="Severity"
                name="severitySelection"
                component={FormDropDownList}
                validator={requiredValidator}
                data={severityList}
                disabled={isLoading || item.isSystem}
                textField="label"
                dataItemKey="value"
                hint={
                  item.isSystem
                    ? "Message and Severity cannot be edited on system notifications"
                    : ""
                }
                onChange={handleSeverityChange}
              />

              <Field
                label="Start"
                name="start"
                component={FormDateTimePicker}
                validator={requiredValidator}
                disabled={isLoading}
                min={new Date()}
              />

              <div style={{ display: "flex " }}>
                <Checkbox
                  checked={isIndefinite}
                  disabled={isLoading}
                  style={{ marginRight: 5 }}
                  onChange={(e) => handleClickIndefinite(e, formRenderProps)}
                />
                <Label>Run Indefinitely</Label>
              </div>

              {!isIndefinite && (
                <Field
                  label="End"
                  name="end"
                  component={FormDateTimePicker}
                  disabled={isLoading}
                  min={new Date()}
                />
              )}

              <div style={{ marginTop: "1rem" }}>
                <Label>Preview</Label>
                <NotificationBanner items={[preview]} />
              </div>

              <div>{saveNotificationApi.error && <Error>{saveNotificationApi.error}</Error>}</div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  paddingTop: "2rem",
                }}
              >
                <LoadingIndicator loading={isLoading} />

                {!isLoading && (
                  <>
                    <Button
                      themeColor="base"
                      fillMode="solid"
                      disabled={isLoading}
                      onClick={props.cancelEdit}
                    >
                      Cancel
                    </Button>

                    <Button
                      themeColor="primary"
                      type="submit"
                      disabled={!formRenderProps.allowSubmit}
                    >
                      Save
                    </Button>
                  </>
                )}
              </div>
            </FormElement>
          )}
        ></Form>
      </div>
    </Dialog>
  );
};
